import React from "react";
import { fetchJournalCaptureHistory, formatPublicationDate } from "../lib/productUpdatesApi.js";
import { buildReportActionCaptureKey, DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME, DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS } from "../lib/dispatchCaptureArrivalHighlight.js";
import { publishDispatchCaptureArrival } from "../lib/dispatchCaptureStatusEvents.js";
import useDispatchCaptureArrivalHighlight from "../hooks/useDispatchCaptureArrivalHighlight.js";
import { formatCaptureActivityGroupLabel, groupCaptureItemsByActivityDate } from "../utils/dispatchCaptureListGroups.js";
import { ImportContextItem } from "./SollioUniagOrderImportHistory.jsx";

const CLOSE_METHOD_LABELS = { cross: "Croix", understood: "Compris", journal: "Voir le Journal", escape: "Échap" };
const BUTTON = "rounded-[4px] border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const SURFACE = "grid min-w-0 gap-2 rounded-[4px] border border-slate-200 bg-white px-4 py-3";
const PAGE_SIZE = 25;

export function JournalCaptureCard({ item, isNew = false }) {
  return <article className={`${SURFACE} ${isNew ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME : ""}`}
    data-journal-capture-card="true" data-dispatch-capture-arrival={isNew ? "true" : undefined}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="m-0 text-[0.86rem] font-bold text-slate-950">Annonce consultée et fermée</h3>
      <time className="text-[0.72rem] text-slate-600" dateTime={item.created_at}>{formatPublicationDate(item.created_at)}</time>
    </div>
    <div className="grid min-w-0 grid-cols-1 gap-1 sm:grid-cols-3">
      <ImportContextItem label="Utilisateur" value={item.actor_label || item.actor_email || item.actor_user_id} wrap />
      <ImportContextItem label="Fermeture" value={CLOSE_METHOD_LABELS[item.close_method] || item.close_method} wrap />
      <ImportContextItem label="Publication" value={`Du ${formatPublicationDate(item.published_at)}`} wrap />
    </div>
    <ul className="m-0 grid min-w-0 gap-0.5 pl-4 text-[0.78rem] text-slate-700">
      {(item.titles || []).map((title, index) => <li className="break-words" key={index}>{title}</li>)}
    </ul>
  </article>;
}

export default function JournalCaptureHistory() {
  const [page, setPage] = React.useState(1);
  const [history, setHistory] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const sequence = React.useRef(0);
  const load = React.useCallback(async ({ quiet = false } = {}) => {
    const request = ++sequence.current;
    if (!quiet) { setLoading(true); setError(""); }
    try {
      const result = await fetchJournalCaptureHistory({ page, pageSize: PAGE_SIZE });
      if (request === sequence.current) { setHistory(result); setError(""); }
    } catch (failure) {
      if (request === sequence.current && !quiet) setError(failure.message || "Impossible de charger les confirmations du Journal.");
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }, [page]);
  React.useEffect(() => {
    void load();
    const refresh = () => { if (document.visibilityState !== "hidden") void load({ quiet: true }); };
    const timer = setInterval(refresh, DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => { ++sequence.current; clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [load]);
  const items = history?.page === page ? history.items || [] : [];
  const keys = React.useMemo(() => items.map(buildReportActionCaptureKey), [items]);
  const highlighted = useDispatchCaptureArrivalHighlight(keys, { enabled: history?.page === page,
    resetKey: page, view: "journal", onArrival: () => publishDispatchCaptureArrival("journal") });
  const groups = groupCaptureItemsByActivityDate(items, item => item.created_at);
  const total = history?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return <div className="grid min-w-0 gap-3" data-journal-capture-history="true" aria-busy={loading}>
    <section className={SURFACE}>
      <h2 className="m-0 text-[0.86rem] font-bold text-slate-950">Journal · {total} confirmation{total > 1 ? "s" : ""}</h2>
      <p className="m-0 text-[0.72rem] text-slate-600">Fermetures volontaires des annonces. La confirmation atteste l’interaction avec la fenêtre, sans garantir la lecture de tous les textes.</p>
    </section>
    {error ? <section className={SURFACE} role="alert"><p className="m-0 text-sm text-rose-700">{error}</p>
      <div><button className={BUTTON} onClick={() => void load()}>Réessayer</button></div></section>
      : loading && history?.page !== page ? <p role="status" className="text-sm text-slate-600">Chargement des confirmations…</p>
      : groups.length ? groups.map(group => <section key={group.activityDate || "unspecified"}
        className="grid min-w-0 gap-2 rounded-[4px] border border-slate-200 bg-slate-50/90 p-2.5">
        <h3 className="m-0 rounded-[4px] border border-slate-300 bg-slate-200/90 px-3 py-2 text-sm font-bold text-slate-950">{formatCaptureActivityGroupLabel(group.activityDate)}</h3>
        {group.items.map(item => <JournalCaptureCard key={item.audit_id} item={item} isNew={highlighted.has(buildReportActionCaptureKey(item))} />)}
      </section>) : <section className={SURFACE}><p className="m-0 text-sm text-slate-600">Aucune confirmation du Journal à afficher.</p></section>}
    {total > 0 && <nav aria-label="Pagination du Journal" className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-[4px] border border-slate-200 bg-white px-4 py-3">
      <span className="text-xs text-slate-600">Page {page} sur {pageCount} · {total} confirmation{total > 1 ? "s" : ""}</span>
      <div className="flex gap-2"><button className={BUTTON} disabled={loading || page <= 1} onClick={() => setPage(value => value - 1)}>Précédente</button>
        <button className={BUTTON} disabled={loading || !history?.has_more} onClick={() => setPage(value => value + 1)}>Suivante</button></div>
    </nav>}
  </div>;
}
